import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/apiClient";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

export default function VerifyEmailPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");
  const [status, setStatus] = useState("verifying"); // verifying | success | error
  const [errorMsg, setErrorMsg] = useState("");
  const [email, setEmail] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) {
        if (!cancelled) {
          setStatus("error");
          setErrorMsg("Falta el token de verificación.");
        }
        return;
      }
      try {
        const { data } = await api.get(`/auth/verify-email?token=${encodeURIComponent(token)}`);
        if (!cancelled) {
          setStatus("success");
          setEmail(data?.email || "");
          toast.success("¡Email confirmado!");
        }
      } catch (e) {
        if (!cancelled) {
          setStatus("error");
          setErrorMsg(formatApiError(e?.response?.data?.detail) || "Token inválido o expirado.");
        }
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  return (
    <div className="container-prose py-20" data-testid="verify-email-page">
      <div className="max-w-md mx-auto border border-gray-200 bg-white p-8 text-center">
        <div className="flex justify-center mb-6">
          <Link to="/" aria-label="JustSapiens Procure">
            <img src="/brand/logo-stacked-positive.png" alt="JustSapiens Procure" className="h-24 w-auto object-contain" />
          </Link>
        </div>

        {status === "verifying" && (
          <div data-testid="verify-state-verifying">
            <h1 className="font-serif text-2xl text-gray-900 mb-3">Verificando tu cuenta…</h1>
            <p className="text-sm text-gray-600">Un momento, estamos confirmando tu email.</p>
            <div className="mt-6 flex justify-center">
              <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
            </div>
          </div>
        )}

        {status === "success" && (
          <div data-testid="verify-state-success">
            <div className="text-emerald-600 text-5xl mb-4">✓</div>
            <h1 className="font-serif text-2xl text-gray-900 mb-3">¡Cuenta verificada!</h1>
            <p className="text-sm text-gray-600 mb-2">
              Tu email <strong>{email}</strong> ha sido confirmado correctamente.
            </p>
            <p className="text-sm text-gray-600 mb-6">
              Ya puedes iniciar sesión y empezar a usar JustSapiens Procure.
            </p>
            <Button
              onClick={() => navigate("/login", { replace: true })}
              className="w-full rounded-sm h-11"
              data-testid="goto-login-btn"
            >
              Iniciar sesión
            </Button>
          </div>
        )}

        {status === "error" && (
          <div data-testid="verify-state-error">
            <div className="text-rose-600 text-5xl mb-4">✕</div>
            <h1 className="font-serif text-2xl text-gray-900 mb-3">No pudimos verificar tu cuenta</h1>
            <p className="text-sm text-gray-600 mb-2">{errorMsg}</p>
            <p className="text-sm text-gray-600 mb-6">
              El enlace pudo haber expirado (válido por 24h) o ya fue usado. Solicita un nuevo enlace desde la página de espera.
            </p>
            <div className="flex flex-col gap-2">
              <Button
                onClick={() => navigate("/verify-email-pending", { replace: true })}
                className="w-full rounded-sm h-11"
                data-testid="goto-pending-btn"
              >
                Solicitar nuevo enlace
              </Button>
              <Link to="/login" className="text-sm text-gray-500 hover:text-primary">Volver a iniciar sesión</Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
